/**
 * Canonical permission registry for the LIMS platform.
 *
 * This is the SINGLE source of truth for permission keys, shared by the backend
 * (which enforces them) and the frontend (which uses them only to decide what to
 * render). Hiding a control in the frontend is never a security mechanism — the
 * backend re-validates every protected operation independently.
 *
 * Design rules:
 *  - Keys are stable strings. Adding a permission is additive and safe; renaming
 *    one is a breaking change requiring a migration.
 *  - `results.review` and `results.approve` are DELIBERATELY SEPARATE. Holding one
 *    never implies the other, and each action is recorded separately even when the
 *    same person performs both. See PLAN.md section 5 (C7/C8).
 *  - Roles are convenience bundles. The Manager may grant or revoke any individual
 *    permission per user without creating a new role.
 */
// ---------------------------------------------------------------------------
// Permission keys
// ---------------------------------------------------------------------------
export const PERMISSIONS = {
    // --- Platform administration ---
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_UPDATE: 'settings.update',
    BRANCHES_MANAGE: 'branches.manage',
    DEPARTMENTS_MANAGE: 'departments.manage',
    NUMBERING_MANAGE: 'numbering.manage',
    // --- Users and permissions ---
    USERS_VIEW: 'users.view',
    USERS_CREATE: 'users.create',
    USERS_UPDATE: 'users.update',
    USERS_DEACTIVATE: 'users.deactivate',
    PERMISSIONS_MANAGE: 'permissions.manage',
    // --- Audit ---
    AUDIT_VIEW: 'audit.view',
    // --- Files and storage ---
    /**
     * See the metadata of a stored file, and download it. Every download is
     * re-checked server-side against the caller's own laboratory — this key is
     * the permission, never the tenant boundary.
     */
    FILES_VIEW: 'files.view',
    FILES_UPLOAD: 'files.upload',
    /** Soft-delete a stored file. The row and the audit trail both survive. */
    FILES_DELETE: 'files.delete',
    /**
     * Storage administration: usage against quota, orphan detection, retention
     * rules and the cleanup run. DELIBERATELY separate from `files.view` — being
     * able to read a document is not being able to change how long the
     * laboratory keeps every document.
     */
    STORAGE_MANAGE: 'storage.manage',
    // --- Clients and projects ---
    CLIENTS_VIEW: 'clients.view',
    CLIENTS_CREATE: 'clients.create',
    CLIENTS_UPDATE: 'clients.update',
    CLIENTS_SUSPEND: 'clients.suspend',
    PROJECTS_VIEW: 'projects.view',
    PROJECTS_CREATE: 'projects.create',
    PROJECTS_UPDATE: 'projects.update',
    // --- Catalogue and pricing ---
    CATALOGUE_VIEW: 'catalogue.view',
    CATALOGUE_MANAGE: 'catalogue.manage',
    /** Overriding a catalogue base price requires this permission AND a recorded reason. */
    PRICING_OVERRIDE: 'pricing.override',
    PRICING_AGREEMENTS_VIEW: 'pricing_agreements.view',
    PRICING_AGREEMENTS_MANAGE: 'pricing_agreements.manage',
    // --- Quotations ---
    QUOTATIONS_VIEW: 'quotations.view',
    QUOTATIONS_CREATE: 'quotations.create',
    QUOTATIONS_UPDATE: 'quotations.update',
    QUOTATIONS_SEND: 'quotations.send',
    /** Recording the client's response. Printing a quotation is NEVER acceptance. */
    QUOTATIONS_RECORD_RESPONSE: 'quotations.record_response',
    /**
     * Soft-deleting a quotation the client REJECTED, with a reason (D210). The
     * row is kept and stays auditable. Manager only by default (D214).
     */
    QUOTATIONS_DELETE: 'quotations.delete',
    // --- Work orders ---
    WORK_ORDERS_VIEW: 'work_orders.view',
    WORK_ORDERS_CREATE: 'work_orders.create',
    WORK_ORDERS_UPDATE: 'work_orders.update',
    WORK_ORDERS_CANCEL: 'work_orders.cancel',
    // --- Samples ---
    SAMPLES_VIEW: 'samples.view',
    SAMPLES_RECEIVE: 'samples.receive',
    SAMPLES_UPDATE: 'samples.update',
    /** Accept, accept with notes, hold, or reject a sample. */
    SAMPLES_DECIDE: 'samples.decide',
    LABELS_PRINT: 'labels.print',
    SAMPLE_STORAGE_MANAGE: 'sample_storage.manage',
    /**
     * Voiding a registered sample, with a reason (D211). Its number, receipt and
     * history are kept; it takes no further work. Manager only by default (D214).
     */
    SAMPLES_VOID: 'samples.void',
    // --- Assignment and execution ---
    ASSIGNMENTS_VIEW: 'assignments.view',
    ASSIGNMENTS_CREATE: 'assignments.create',
    ASSIGNMENTS_REASSIGN: 'assignments.reassign',
    /** Enter readings and results, save drafts, submit for review. */
    RESULTS_ENTER: 'results.enter',
    RESULTS_VIEW: 'results.view',
    /**
     * Technical review. SEPARATE from approval — see module note above.
     * Grants: technical acceptance, return for correction, require retest.
     */
    RESULTS_REVIEW: 'results.review',
    /**
     * Final approval. SEPARATE from review — see module note above.
     * Grants: approve a technically accepted result, or return it with a reason.
     */
    RESULTS_APPROVE: 'results.approve',
    /** Open a recorded correction procedure on an already-approved result. */
    RESULTS_CORRECT: 'results.correct',
    // --- Reports ---
    REPORTS_VIEW: 'reports.view',
    REPORTS_PREPARE: 'reports.prepare',
    /** Issue an immutable report version. Distinct from delivering it. */
    REPORTS_ISSUE: 'reports.issue',
    /** Deliver or release an issued report. Distinct from issuing it. */
    REPORTS_DELIVER: 'reports.deliver',
    REPORTS_CORRECT: 'reports.correct',
    REPORTS_CANCEL: 'reports.cancel',
    REPORTS_SUSPEND: 'reports.suspend',
    TEMPLATES_VIEW: 'templates.view',
    TEMPLATES_MANAGE: 'templates.manage',
    // --- Finance ---
    /** Gate for seeing any monetary figure, including on dashboards. */
    FINANCE_VIEW: 'finance.view',
    INVOICES_CREATE: 'invoices.create',
    INVOICES_ISSUE: 'invoices.issue',
    INVOICES_CANCEL: 'invoices.cancel',
    PAYMENTS_RECORD: 'payments.record',
    PAYMENTS_ADJUST: 'payments.adjust',
    STATEMENTS_VIEW: 'statements.view',
    // --- Equipment ---
    EQUIPMENT_VIEW: 'equipment.view',
    EQUIPMENT_MANAGE: 'equipment.manage',
    /**
     * Authorise use of suspended or out-of-calibration equipment.
     * Requires a recorded reason. Deliberately NOT granted to technicians —
     * an exception must never be an override available to everyone.
     */
    EQUIPMENT_OVERRIDE_CALIBRATION: 'equipment.override_calibration',
    CALIBRATION_MANAGE: 'calibration.manage',
    /**
     * Phase 12 (D166). Report a fault on an instrument. Granted widely: anyone
     * at a bench must be able to say "this is broken". Reporting does NOT
     * suspend the instrument (D168) — that stays `equipment.manage`.
     */
    EQUIPMENT_REPORT_FAULT: 'equipment.report_fault',
    // --- Quality ---
    QUALITY_VIEW: 'quality.view',
    QUALITY_MANAGE: 'quality.manage',
    /**
     * Phase 12 (D166). Raise a complaint, an observation or a non-conformity.
     * DELIBERATELY separate from `quality.view`: anyone may raise one without
     * being able to read, or manage, the whole register.
     */
    QUALITY_RAISE: 'quality.raise',
    // --- Data import ---
    /**
     * Phase 12 (D175). Bulk import of clients, projects and catalogue entries.
     * Required ON TOP OF the create permission of what is imported.
     */
    DATA_IMPORT: 'data.import',
    // --- Client portal administration ---
    PORTAL_ACCOUNTS_MANAGE: 'portal_accounts.manage',
    // --- Dashboards ---
    DASHBOARD_VIEW: 'dashboard.view',
};
/** Every permission key, for validation and seeding. */
export const ALL_PERMISSIONS = Object.freeze(Object.values(PERMISSIONS));
const PERMISSION_SET = new Set(ALL_PERMISSIONS);
/** Type guard: is this string a known permission key? */
export function isPermissionKey(value) {
    return PERMISSION_SET.has(value);
}
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
/**
 * The four internal laboratory roles. Client portal users are NOT an internal
 * role — they authenticate through a separate portal context.
 */
export const ROLES = {
    MANAGER: 'manager',
    RECEPTION: 'reception',
    SUPERVISOR: 'supervisor',
    TECHNICIAN: 'technician',
};
export const ALL_ROLES = Object.freeze(Object.values(ROLES));
export function isRole(value) {
    return ALL_ROLES.includes(value);
}
// ---------------------------------------------------------------------------
// Default role bundles
// ---------------------------------------------------------------------------
const RECEPTION_DEFAULTS = [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CLIENTS_VIEW,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.PROJECTS_VIEW,
    PERMISSIONS.PROJECTS_CREATE,
    PERMISSIONS.PROJECTS_UPDATE,
    PERMISSIONS.CATALOGUE_VIEW,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.FILES_DELETE,
    PERMISSIONS.QUOTATIONS_VIEW,
    PERMISSIONS.QUOTATIONS_CREATE,
    PERMISSIONS.QUOTATIONS_UPDATE,
    PERMISSIONS.QUOTATIONS_SEND,
    PERMISSIONS.QUOTATIONS_RECORD_RESPONSE,
    PERMISSIONS.PRICING_AGREEMENTS_VIEW,
    PERMISSIONS.WORK_ORDERS_VIEW,
    PERMISSIONS.WORK_ORDERS_CREATE,
    PERMISSIONS.WORK_ORDERS_UPDATE,
    PERMISSIONS.SAMPLES_VIEW,
    PERMISSIONS.SAMPLES_RECEIVE,
    PERMISSIONS.SAMPLES_UPDATE,
    PERMISSIONS.SAMPLES_DECIDE,
    PERMISSIONS.LABELS_PRINT,
    PERMISSIONS.SAMPLE_STORAGE_MANAGE,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_DELIVER,
    PERMISSIONS.EQUIPMENT_VIEW,
    // Phase 12 (D166): anyone may raise a quality record or report a fault.
    PERMISSIONS.QUALITY_RAISE,
    PERMISSIONS.EQUIPMENT_REPORT_FAULT,
];
const SUPERVISOR_DEFAULTS = [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CLIENTS_VIEW,
    PERMISSIONS.PROJECTS_VIEW,
    PERMISSIONS.CATALOGUE_VIEW,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.FILES_DELETE,
    PERMISSIONS.WORK_ORDERS_VIEW,
    PERMISSIONS.SAMPLES_VIEW,
    PERMISSIONS.ASSIGNMENTS_VIEW,
    PERMISSIONS.ASSIGNMENTS_CREATE,
    PERMISSIONS.ASSIGNMENTS_REASSIGN,
    PERMISSIONS.RESULTS_VIEW,
    // Review by default; approval is deliberately NOT included.
    PERMISSIONS.RESULTS_REVIEW,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_PREPARE,
    PERMISSIONS.EQUIPMENT_VIEW,
    PERMISSIONS.CALIBRATION_MANAGE,
    PERMISSIONS.QUALITY_VIEW,
    PERMISSIONS.QUALITY_RAISE,
    PERMISSIONS.EQUIPMENT_REPORT_FAULT,
];
const TECHNICIAN_DEFAULTS = [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.SAMPLES_VIEW,
    PERMISSIONS.ASSIGNMENTS_VIEW,
    PERMISSIONS.RESULTS_VIEW,
    PERMISSIONS.RESULTS_ENTER,
    PERMISSIONS.CATALOGUE_VIEW,
    PERMISSIONS.EQUIPMENT_VIEW,
    PERMISSIONS.FILES_VIEW,
    PERMISSIONS.FILES_UPLOAD,
    PERMISSIONS.QUALITY_RAISE,
    PERMISSIONS.EQUIPMENT_REPORT_FAULT,
    // Deliberately NOT files.delete: a technician attaches evidence to their own
    // work, and removing evidence is a different act from producing it.
];
/**
 * Default permissions per role.
 *
 * The Manager holds every permission. Default routing is Supervisor reviews and
 * Manager approves, but the Manager may grant `results.review` or
 * `results.approve` to any user, together or separately.
 *
 * These are STARTING POINTS only. Per-user grants and revocations are stored
 * separately and take precedence.
 */
export const DEFAULT_ROLE_PERMISSIONS = Object.freeze({
    [ROLES.MANAGER]: ALL_PERMISSIONS,
    [ROLES.RECEPTION]: Object.freeze(RECEPTION_DEFAULTS),
    [ROLES.SUPERVISOR]: Object.freeze(SUPERVISOR_DEFAULTS),
    [ROLES.TECHNICIAN]: Object.freeze(TECHNICIAN_DEFAULTS),
});
/**
 * Resolve a user's effective permissions.
 *
 * Precedence: role defaults, then explicit grants, then explicit revocations.
 * Revocation wins, so a permission can always be taken away from a user —
 * including one their role would otherwise give them.
 *
 * Unknown keys are ignored rather than throwing, so a stale grant left behind by
 * a renamed permission cannot escalate privilege or break login.
 */
export function resolveEffectivePermissions(role, overrides = {}) {
    const effective = new Set(DEFAULT_ROLE_PERMISSIONS[role]);
    for (const key of overrides.granted ?? []) {
        if (isPermissionKey(key))
            effective.add(key);
    }
    for (const key of overrides.revoked ?? []) {
        if (isPermissionKey(key))
            effective.delete(key);
    }
    return effective;
}
/** Does this resolved permission set include the required permission? */
export function hasPermission(effective, required) {
    return effective.has(required);
}
/** Does this resolved permission set include every one of the required permissions? */
export function hasAllPermissions(effective, required) {
    return required.every((key) => effective.has(key));
}
/** Does this resolved permission set include at least one of the required permissions? */
export function hasAnyPermission(effective, required) {
    return required.some((key) => effective.has(key));
}
//# sourceMappingURL=index.js.map